// library imports
import { useState, useEffect, useContext, useRef } from "react";
import { Outlet, useLocation, useNavigate } from "react-router-dom";
import {
  Layout,
  Flex,
  Card,
  Popover,
  Avatar,
  Menu,
  Button,
  Row,
  Col,
  theme,
  Skeleton,
} from "antd";
import { UnorderedListOutlined, UserOutlined } from "@ant-design/icons";
// context imports
import { IndexContext } from "../context/IndexContext";
// hook imports
import useFetch from "../hooks/useFetch";
import useNavMenu from "./NavMenu";
import useAuthCheck from "../hooks/useAuthCheck";
import useSiteSelection from "../hooks/useSiteSelection";
import useSiteSubscription from "../hooks/useSiteSubscription";
import useHasInverter from "../hooks/useHasInverter";
import useHasPvPowerMeter from "../hooks/useHasPvPowerMeter";
import useHasWeatherStation from "../hooks/useHasWeatherStation";
import useHasInverterSelected from "../hooks/useHasInverterSelected";
import useHasWeatherStationSelected from "../hooks/useHasWeatherStationSelected";
import useHasGensetSelected from "../hooks/useHasGensetSelected";
import useHasTnbSelected from "../hooks/useHasTnbSelected";
import useHasGenset from "../hooks/useHasGenset";
import useHasBess from "../hooks/useHasBess";
import useHasTnb from "../hooks/useHasTnb";
// asset imports
import reveaiLightTheme from "../assets/reveai_light_theme.png";
import reveaiDarkTheme from "../assets/reveai_dark_theme.png";
// component imports
import ProfileMenu from "./ProfileMenu";
import SiteSelection from "./SiteSelection";
import { CONFIGS } from "../configs/configs";

/**
 * TO-DO: when sider menu is collapsed, transition is not smooth (SiteSelection component is shown very fast but content is very slow)
 */
function MainLayout() {
  const [fetchWrapper] = useFetch();

  // user profile image path
  const [userProfileImagePath, setUserProfileImagePath] = useState(null);

  // client logo image path
  const [showClientLogo, setShowClientLogo] = useState(false);
  const [clientLogoDarkTheme, setClientLogoDarkTheme] = useState(null);
  const [clientLogoLightTheme, setClientLogoLightTheme] = useState(null);

  // create a ref to point to the layout DOM
  const layoutRef = useRef();

  // load auth checker
  const [authenticationState, getAuthenticationState] = useAuthCheck();

  // load site selection
  const [siteSelected, setSiteSelected, siteList, getSiteSelectionLoading] =
    useSiteSelection();

  // disable site selection
  const [disabledSiteSelection, setDisabledSiteSelection] = useState(false);

  // load has inverter
  const [hasInverter, getHasInverter, getHasInverterLoading] = useHasInverter();

  // load has PV power meter
  const [hasPvPowerMeter, getHasPvPowerMeter, getHasPvPowerMeterLoading] =
    useHasPvPowerMeter();

  // load has weather station
  const [hasWeatherStation, getHasWeatherStation, getHasWeatherStationLoading] =
    useHasWeatherStation();

  // load has BESS
  const [hasBess, getHasBess, getHasBessLoading] = useHasBess();

  // load has tnb
  const [hasTnb, getHasTnb, getHasTnbLoading] = useHasTnb();

  // load has inverter selected
  const [
    hasInverterSelected,
    getHasInverterSelected,
    getHasInverterSelectedLoading,
  ] = useHasInverterSelected();

  // load has weather station selected
  const [
    hasWeatherStationSelected,
    getHasWeatherStationSelected,
    getHasWeatherStationSelectedLoading,
  ] = useHasWeatherStationSelected();

  // load has genset
  const [hasGenset, getHasGenset, getHasGensetLoading] = useHasGenset();

  // load has genset selected
  const [hasGensetSelected, getHasGensetSelected, getHasGensetSelectedLoading] =
    useHasGensetSelected();

  // load has grid selected
  const [hasTnbSelected, getHasTnbSelected, getHasTnbSelectedLoading] =
    useHasTnbSelected();

  // load site subscription
  const [
    getFeatureSubscriptionLoading,
    subscribePpaBilling,
    subscribeAiPvDiagnosis,
    subscribeCarbonManagement,
    subscribeAiBess,
    getFeatureSubscription,
  ] = useSiteSubscription();

  // get nav menu, pass necessary arguments to useNavMenu function so that NavMenu will be changed according to site subscription
  const [NavMenu, header_height] = useNavMenu({
    authenticationState: authenticationState,
    subscribePpaBilling: subscribePpaBilling,
    subscribeAiPvDiagnosis: subscribeAiPvDiagnosis,
    subscribeCarbonManagement: subscribeCarbonManagement,
    subscribeAiBess: subscribeAiBess,
    siteSelected: siteSelected,
  });

  // use context
  const index_context = useContext(IndexContext);

  // get the token of current theme
  const { token } = theme.useToken();

  // for navigation
  const navigate = useNavigate();
  const location = useLocation();
  // for top menu collapse to sidebar
  const [collapsed, setCollapsed] = useState(true);
  // for getting current selected menu key
  const [menuSelectedKey, setMenuSelectedKey] = useState("Dashboard");

  // styles
  const headerStyle = {
    textAlign: "center",
    height: header_height,
    paddingInline: 0,
    lineHeight: header_height,
    position: "sticky",
    top: 0,
    zIndex: 1,
  };

  const contentStyle = {
    height: "calc(100% -" + header_height + " )",
    width: "100vw",
    minWidth: "100vw",
    overflow: "auto",
    padding: 10,
    marginTop:
      collapsed &&
      !location.pathname.includes("Management") &&
      !location.pathname.includes("SiteList") &&
      !location.pathname.includes("ServiceAlarmRecord")
        ? 40
        : 0, // this margin is for site selection
  };

  const layoutStyle = {
    overflow: "hidden",
    padding: 0,
    margin: 0,
    width: "100%",
    height: "100vh",
  };

  /* API to get user profile image path */
  function getUserProfileImagePath() {
    fetchWrapper({
      endpoint_url: "layout/getUserProfileImagePath",
      method: "GET",
      set_content_type_header: false,
      onSuccess: (response) => {
        setUserProfileImagePath(response.data.image_path);
      },
    });
  }

  /* API to get client logo image path */
  function getClientLogo() {
    fetchWrapper({
      endpoint_url: "layout/getClientLogo",
      method: "GET",
      set_content_type_header: false,
      onSuccess: (response) => {
        setShowClientLogo(response.data.show_client_logo);
        setClientLogoDarkTheme(response.data.image_path_dark_theme);
        setClientLogoLightTheme(response.data.image_path_light_theme);
      },
    });
  }

  useEffect(() => {
    const path = location.pathname.split("/").pop();
    setMenuSelectedKey(path.charAt(0).toUpperCase() + path.slice(1));
  }, [location]);

  useEffect(() => {
    if (siteSelected) {
      getFeatureSubscription({ siteSelected: siteSelected });
      getHasPvPowerMeter({ siteSelected: siteSelected });
      getHasInverter({ siteSelected: siteSelected });
      getHasWeatherStation({ siteSelected: siteSelected });
      getHasBess({ siteSelected: siteSelected });
      getHasTnb({ siteSelected: siteSelected });
      getHasTnbSelected({ siteSelected: siteSelected });
      getHasInverterSelected({ siteSelected: siteSelected });
      getHasWeatherStationSelected({ siteSelected: siteSelected });
      getHasGenset({ siteSelected: siteSelected });
      getHasGensetSelected({ siteSelected: siteSelected });
    }
  }, [siteSelected]);

  useEffect(() => {
    getUserProfileImagePath();
    getClientLogo();
  }, []);

  return (
    <Layout style={layoutStyle} ref={layoutRef}>
      <Layout.Header style={headerStyle}>
        <Card
          style={{
            height: "100%",
            width: "100%",
            borderRadius: 0,
            margin: 0,
          }}
          styles={{
            body: {
              height: "100%",
              width: "100%",
              borderRadius: 0,
              paddingBlock: 0,
              paddingLeft: 12,
            },
          }}
          bordered={false}
        >
          <Row style={{ height: "100%" }}>
            <Col xl={0} flex="52px">
              <Flex align="center" style={{ height: "100%", width: "100%" }}>
                <Button
                  icon={<UnorderedListOutlined />}
                  type="text"
                  size="large"
                  onClick={() => setCollapsed(!collapsed)}
                />
              </Flex>
            </Col>
            <Col flex="auto" style={{ height: "100%", paddingLeft: 0 }}>
              <Flex
                justify="space-between"
                align="center"
                style={{ height: "100%", width: "100%" }}
              >
                <Flex gap="12px" align="center">
                  <img
                    src={
                      index_context.isLightTheme
                        ? reveaiLightTheme
                        : reveaiDarkTheme
                    }
                    alt="Reveai"
                    style={{
                      height: "12px",
                    }}
                  />

                  {showClientLogo &&
                    (index_context.isLightTheme
                      ? clientLogoLightTheme && (
                          <img
                            src={`${CONFIGS.backend_url}device_management/getDeviceImage?Path=${clientLogoLightTheme}`}
                            alt="client_logo"
                            style={{ height: "28px" }}
                          />
                        )
                      : clientLogoDarkTheme && (
                          <img
                            src={`${CONFIGS.backend_url}device_management/getDeviceImage?Path=${clientLogoDarkTheme}`}
                            alt="client_logo"
                            style={{ height: "28px" }}
                          />
                        ))}
                </Flex>
                <Row style={{ height: "100%" }}>
                  <Col
                    style={{ height: "100%" }}
                    xs={0}
                    sm={0}
                    md={0}
                    lg={0}
                    xl={24}
                  >
                    <Menu
                      style={{
                        height: "100%",
                        width: 1200,
                        borderBottom: 0,
                      }}
                      mode="horizontal"
                      items={NavMenu}
                      onClick={(item) => {
                        // check login user before navigate
                        getAuthenticationState({});

                        let path = "";
                        item.keyPath.reverse().map((value) => {
                          path = path + "/" + value;
                        });
                        navigate(path);
                      }}
                      onSelect={(item) => {
                        setMenuSelectedKey(item.key);
                      }}
                      selectedKeys={menuSelectedKey}
                    ></Menu>
                  </Col>
                </Row>
                <Popover
                  placement="bottom"
                  contentStyle={{ marginTop: "50px" }}
                  content={<ProfileMenu />}
                  arrow={false}
                  trigger="hover"
                  overlayInnerStyle={{
                    borderRadius: 0,
                    marginTop: 10,
                    padding: 0,
                  }}
                >
                  {userProfileImagePath ? (
                    <Avatar
                      src={
                        <img
                          src={`${CONFIGS.backend_url}device_management/getDeviceImage?Path=${userProfileImagePath}`}
                          alt="avatar"
                        />
                      }
                    />
                  ) : (
                    <Avatar
                      style={{ backgroundColor: token["blue-6"] }}
                      icon={<UserOutlined />}
                    />
                  )}
                </Popover>
              </Flex>
            </Col>
          </Row>
          <Row
            style={{
              display:
                !collapsed ||
                location.pathname.includes("Management") || // hide site selection when collapsed or when route contains 'Management'
                location.pathname.includes("SiteList") || // hide site selection when route contains 'SiteList'
                location.pathname.includes("ServiceAlarmRecord") // hide site selection on Site Alarm page
                  ? "none"
                  : "block",
              paddingLeft: !collapsed ? 310 : 0,
            }}
          >
            <SiteSelection
              siteSelected={siteSelected}
              setSiteSelected={setSiteSelected}
              siteList={siteList}
              loading={getSiteSelectionLoading}
              menuSelectedKey={menuSelectedKey}
              disabled={disabledSiteSelection}
            />
          </Row>
        </Card>
      </Layout.Header>
      <Layout>
        <Layout.Sider
          breakpoint="xl"
          collapsedWidth="0"
          width="300"
          collapsible
          trigger={null}
          collapsed={collapsed}
          onBreakpoint={() => setCollapsed(true)}
        >
          <Menu
            mode="inline"
            inlineIndent={32}
            style={{
              height: "100%",
            }}
            items={NavMenu}
            onClick={(item) => {
              // check login user before navigate
              getAuthenticationState({});
              setCollapsed(true);

              let path = "";
              item.keyPath.reverse().map((value) => {
                path = path + "/" + value;
              });
              navigate(path);

              setDisabledSiteSelection(false);
            }}
            onSelect={(item) => setMenuSelectedKey(item.key)}
            selectedKeys={menuSelectedKey}
          ></Menu>
        </Layout.Sider>
        <Layout.Content style={contentStyle}>
          {/* add layoutRef into the IndexContext */}
          {collapsed === true ? (
            <IndexContext.Provider
              value={{
                ...index_context,
                layoutRef: layoutRef,
                authenticationState: authenticationState,

                siteSelected: siteSelected,
                setSiteSelected: setSiteSelected,
                setDisabledSiteSelection: setDisabledSiteSelection,
                siteList: siteList,

                subscribePpaBilling: subscribePpaBilling,
                subscribeCarbonManagement: subscribeCarbonManagement,
                subscribeAiPvDiagnosis: subscribeAiPvDiagnosis,
                subscribeAiBess: subscribeAiBess,
                getFeatureSubscriptionLoading: getFeatureSubscriptionLoading,

                hasInverter: hasInverter,
                getHasInverterLoading: getHasInverterLoading,
                getHasInverter: getHasInverter,

                hasInverterSelected: hasInverterSelected,
                getHasInverterSelectedLoading: getHasInverterSelectedLoading,
                getHasInverterSelected: getHasInverterSelected,

                hasPvPowerMeter: hasPvPowerMeter,
                getHasPvPowerMeterLoading: getHasPvPowerMeterLoading,
                getHasPvPowerMeter: getHasPvPowerMeter,

                hasWeatherStation: hasWeatherStation,
                getHasWeatherStationLoading: getHasWeatherStationLoading,
                getHasWeatherStation: getHasWeatherStation,

                hasBess: hasBess,
                getHasBessLoading: getHasBessLoading,
                getHasBess: getHasBess,

                hasTnb: hasTnb,
                getHasTnbLoading: getHasTnbLoading,
                getHasTnb: getHasTnb,

                hasTnbSelected: hasTnbSelected,
                getHasTnbSelectedLoading: getHasTnbSelectedLoading,
                getHasTnbSelected: getHasTnbSelected,

                hasWeatherStationSelected: hasWeatherStationSelected,
                getHasWeatherStationSelectedLoading:
                  getHasWeatherStationSelectedLoading,
                getHasWeatherStationSelected: getHasWeatherStationSelected,

                hasGenset: hasGenset,
                getHasGensetLoading: getHasGensetLoading,
                getHasGenset: getHasGenset,

                hasGensetSelected: hasGensetSelected,
                getHasGensetSelectedLoading: getHasGensetSelectedLoading,
                getHasGensetSelected: getHasGensetSelected,
              }}
            >
              <Outlet></Outlet>
            </IndexContext.Provider>
          ) : (
            <Skeleton active={true} paragraph={{ rows: 5 }} /> // when sider menu is shown, display loading
          )}
        </Layout.Content>
      </Layout>
    </Layout>
  );
}

export default MainLayout;
